import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  CalendarDays, 
  Microscope, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  ChevronDown,
  User,
  ClipboardCheck,
  Users,
  Shield,
  FileText,
  Send,
  HelpCircle,
  Bell,
  CreditCard,
  Sun,
  Moon
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { cn, apiRequest } from "../lib/utils";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import WhatsAppButton from "./WhatsAppButton";
import TrialBanner from "./TrialBanner";
import TrialExpiredScreen from "./TrialExpiredScreen";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, superAdminOnly: false, farmaciaOnly: true },
  { path: "/documentos", label: "Documentos e Alvaras", icon: FileText, superAdminOnly: false, farmaciaOnly: true },
  { path: "/itens", label: "Itens Calibráveis", icon: Microscope, superAdminOnly: false, farmaciaOnly: true },
  { path: "/verificacoes", label: "Verificações Diárias", icon: ClipboardCheck, superAdminOnly: false, farmaciaOnly: true },
  { path: "/envios", label: "Envios", icon: Send, superAdminOnly: false, farmaciaOnly: true },
  { path: "/funcionarios", label: "Funcionários", icon: Users, superAdminOnly: false, farmaciaOnly: true },
  { path: "/assinaturas", label: "Assinatura", icon: CreditCard, superAdminOnly: false, farmaciaOnly: true },
  { path: "/admin", label: "Administração", icon: Settings, superAdminOnly: false, farmaciaOnly: true },
  { path: "/suporte", label: "Suporte", icon: HelpCircle, superAdminOnly: false, farmaciaOnly: true, checkNotifications: true },
];

const Sidebar = ({ className, isSuperAdmin, ticketsNaoLidos = 0 }) => {
  const location = useLocation();
  
  // Filtrar itens baseado no tipo de usuário
  const filteredNavItems = navItems.filter(item => {
    // Se é Super Admin, mostra apenas itens que NÃO são exclusivos de farmácia
    if (isSuperAdmin) {
      return !item.farmaciaOnly;
    }
    // Se não é Super Admin, mostra apenas itens que NÃO são exclusivos de Super Admin
    return !item.superAdminOnly;
  });
  
  return (
    <aside className={cn("w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700 flex flex-col", className)}>
      <div className="p-6 border-b border-slate-200 dark:border-slate-700">
        <Link to="/dashboard" className="flex items-center gap-3">
          <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
            <CalendarDays className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-lg text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>Conforme a Resolução</h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">Gestão Documental</p>
          </div>
        </Link>
      </div>
      
      <nav className="flex-1 p-4 space-y-1">
        {filteredNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
          const hasNotification = item.checkNotifications && ticketsNaoLidos > 0;
          
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.path.replace("/", "")}`}
              className={cn(
                "flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200",
                isActive
                  ? "bg-teal-600 text-white shadow-sm"
                  : hasNotification
                    ? "text-amber-600 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                    : "text-slate-600 dark:text-slate-300 hover:bg-teal-50 dark:hover:bg-slate-800 hover:text-teal-700 dark:hover:text-teal-400"
              )}
            >
              <span className="flex items-center gap-3">
                <Icon className="w-5 h-5" />
                {item.label}
              </span>
              {hasNotification && !isActive && (
                <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-amber-500 rounded-full">
                  {ticketsNaoLidos}
                </span>
              )}
            </Link>
          );
        })}
        
        {/* Link Super Admin - só aparece para super admins */}
        {isSuperAdmin && (
          <Link
            to="/super-admin"
            data-testid="nav-super-admin"
            className={cn(
              "flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 mt-4 border-t border-slate-200 dark:border-slate-700 pt-4",
              location.pathname === "/super-admin"
                ? "bg-purple-600 text-white shadow-sm"
                : "text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:text-purple-700"
            )}
          >
            <Shield className="w-5 h-5" />
            Super Admin
          </Link>
        )}
      </nav>
      
      <div className="p-4 border-t border-slate-200 dark:border-slate-700">
        <div className="text-xs text-slate-400 dark:text-slate-500 text-center">
          © 2024 Conforme a Resolução
        </div>
      </div>
    </aside>
  );
};

const Header = ({ isSuperAdmin }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <header className="h-16 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200/50 dark:border-slate-700/50 px-4 lg:px-8 flex items-center justify-between sticky top-0 z-40">
      <div className="flex items-center gap-4">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild className="lg:hidden">
            <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-64">
            <Sidebar isSuperAdmin={isSuperAdmin} />
          </SheetContent>
        </Sheet>
        
        <div className="lg:hidden flex items-center gap-2">
          <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center">
            <CalendarDays className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>Conforme a Resolução</span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {/* Theme Toggle Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="w-9 h-9 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800"
          data-testid="theme-toggle-btn"
          title={theme === "light" ? "Ativar modo escuro" : "Ativar modo claro"}
        >
          {theme === "light" ? (
            <Moon className="w-5 h-5 text-slate-600 dark:text-slate-300" />
          ) : (
            <Sun className="w-5 h-5 text-amber-500" />
          )}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              className="flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-800"
              data-testid="user-menu-btn"
            >
              <div className="w-8 h-8 bg-teal-100 dark:bg-teal-900 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-teal-700 dark:text-teal-300" />
              </div>
              <span className="hidden sm:inline text-sm font-medium text-slate-700 dark:text-slate-200">
                {user?.nome || "Usuário"}
              </span>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{user?.nome}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{user?.email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/configuracoes" className="cursor-pointer">
                <Settings className="w-4 h-4 mr-2" />
                Configurações
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={handleLogout}
              className="text-red-600 cursor-pointer"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = user?.is_super_admin === true;
  const [ticketsNaoLidos, setTicketsNaoLidos] = useState(0);
  const [trialStatus, setTrialStatus] = useState(null);
  const [checkingTrial, setCheckingTrial] = useState(true);
  
  // Buscar status do trial
  useEffect(() => {
    const fetchTrialStatus = async () => {
      if (isSuperAdmin) {
        setCheckingTrial(false);
        return;
      }
      
      try {
        const data = await apiRequest('/trial/status');
        setTrialStatus(data);
      } catch (error) {
        console.error('Erro ao buscar status do trial:', error);
      } finally {
        setCheckingTrial(false);
      }
    };
    
    fetchTrialStatus();
  }, [isSuperAdmin]);
  
  // Buscar contagem de tickets não lidos
  useEffect(() => {
    const fetchTicketsNaoLidos = async () => {
      if (isSuperAdmin) return; // Super admin não precisa dessa notificação
      
      try {
        const data = await apiRequest("/tickets/nao-lidos/count");
        setTicketsNaoLidos(data.count || 0);
      } catch (error) {
        console.error("Erro ao buscar tickets não lidos:", error);
      }
    };
    
    fetchTicketsNaoLidos();
    
    // Verificar a cada 60 segundos
    const interval = setInterval(fetchTicketsNaoLidos, 60000);
    return () => clearInterval(interval);
  }, [isSuperAdmin]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };
  
  // Mostrar tela de trial expirado se necessário
  // Mas permitir acesso à página de assinatura
  // Contas demo (is_permanent_trial) nunca expiram
  const currentPath = window.location.pathname;
  const isAssinaturaPage = currentPath === '/assinatura' || currentPath.startsWith('/checkout');
  const isPermanentTrial = trialStatus?.is_permanent_trial === true;
  
  if (!checkingTrial && trialStatus?.trial_expired && !isSuperAdmin && !isAssinaturaPage && !isPermanentTrial) {
    return <TrialExpiredScreen onLogout={handleLogout} />;
  }
  
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col transition-colors duration-200">
      {/* Trial Banner - aparece no topo */}
      {!isSuperAdmin && <TrialBanner />}
      
      <div className="flex flex-1">
        <Sidebar className="hidden lg:flex" isSuperAdmin={isSuperAdmin} ticketsNaoLidos={ticketsNaoLidos} />
        <div className="flex-1 flex flex-col">
          <Header isSuperAdmin={isSuperAdmin} />
          <main className="flex-1 p-4 lg:p-8 animate-fade-in">
            {children}
          </main>
        </div>
      </div>
      {/* Botão flutuante do WhatsApp */}
      <WhatsAppButton />
    </div>
  );
};

export default Layout;
